import React from 'react';
import PropTypes from 'prop-types';
import { compose, withProps, withStateHandlers, lifecycle } from 'recompose';
import { withScriptjs, withGoogleMap, GoogleMap, Marker, InfoWindow } from 'react-google-maps';
import { DEFAULT_MAP_CENTER } from 'constants/signUp';
import './MapInfoWindow.scss';
import { googleMapUrl } from 'utils/googleMapUtils';
import _ from 'lodash';
import I18N from './I18N';

const MyMapComponent = compose(
  withProps(props => {
    const { googleMapParam, lang } = props.parentprops;
    const url = googleMapUrl(googleMapParam, lang);
    return {
      googleMapURL: url,
      loadingElement: <div style={{ height: '100%' }} className="loading" />,
      containerElement: <div className="map-canvas" />,
      mapElement: <div style={{ height: '100%' }} className="map" />,
    };
  }),
  withStateHandlers(
    state => ({
      isOpen: false,
      whichMarker: 0,
      dragPointPosition: {
        lat: state.parentprops.center
          ? Number(state.parentprops.center.lat) + 0.001
          : DEFAULT_MAP_CENTER.lat,
        lng: state.parentprops.markers.length
          ? Number(state.parentprops.center.lng) - 0.001
          : DEFAULT_MAP_CENTER.lng,
      },
      isShowDragInfo: false,
      markersLength: state.parentprops.markers.length
    }),
    {
      onToggleOpen: ({ isOpen }) => (index, marker) => {
        if (isOpen) {
          return {
            whichMarker: index,
          };
        }
        return {
          isOpen: !isOpen,
          whichMarker: index,
        };
      },
      markDragStart: () => () => ({
        isShowDragInfo: false,
      }),
      markDragEnd: () => (state, dragMark) => {
        state.parentprops.setPosition({
          lat: dragMark.latLng.lat(),
          lng: dragMark.latLng.lng(),
        });
        return {
          dragPointPosition: {
            lat: dragMark.latLng.lat(),
            lng: dragMark.latLng.lng(),
          },
          isShowDragInfo: true,
        };
      },
      updateMarker: () => (newPosition) => ({
        dragPointPosition: newPosition
      }),
      markDragClick: ({ isShowDragInfo }) => () => ({
        isShowDragInfo: !isShowDragInfo,
      }),
      closeInfoWindow: () => () => ({
        isOpen: false,
        isShowDragInfo: false,
      }),
    },
  ),
  withScriptjs,
  withGoogleMap,
  lifecycle({
    componentDidMount() {
      const { comeFrom, center } = this.props.parentprops;
      if (comeFrom !== 'viewOnMap' && _.isEmpty(center)) {
        const { formAddress, setPosition } = this.props.parentprops;
        const { street, suite, city, state, postalCode, countryName } = formAddress;
        const addressString = Object.values({
          street,
          suite,
          city,
          state,
          postalCode,
          countryName,
        }).filter(str => !!str)
          .join(', ');
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: addressString }, (results, status) => {
          if (status === 'OK') {
            if (results[0]) {
              const { location } = results[0].geometry;
              const newPosition = {
                lat: location.lat(),
                lng: location.lng(),
              };
              setPosition(newPosition);
              this.props.updateMarker(newPosition);
            } else {
              setPosition({
                lat: DEFAULT_MAP_CENTER.lat,
                lng: DEFAULT_MAP_CENTER.lng,
              });
            }
          } else {
            setPosition({
              lat: DEFAULT_MAP_CENTER.lat,
              lng: DEFAULT_MAP_CENTER.lng,
            });
          }
        });
      }
    }
  })
)(state => {
  for (let i = 0; i < document.getElementsByClassName('js_raido').length; i++) {
    document.getElementsByClassName('js_raido')[i].addEventListener('click', () => {
      if (state.isOpen) {
        state.closeInfoWindow();
      }
    });
  }
  // if on safari prevent touchmove event
  document.getElementsByClassName('js_listen_map')[0].addEventListener('ontouchmove', event => {
    event.preventDefault();
  });

  const cssDetector = window.getComputedStyle(document.getElementById('css-detector'), null);
  if (cssDetector.visibility !== 'hidden') {
    document.querySelector('.map-canvas').style.height = '500px';
  }

  const setZoom = map => {
    if (!map) return;
    const bounds = new google.maps.LatLngBounds();
    const boundsInit = point =>
      (Array.isArray(point)
        ? point.forEach(boundsInit)
        : bounds.extend(new google.maps.LatLng(point.lat, point.lng)));
    const { center } = state.parentprops;
    if (center) {
      map.panTo(new google.maps.LatLng(center.lat, center.lng));
    } else {
      boundsInit(state.parentprops.paths);
      map.fitBounds(bounds);
      map.panToBounds(bounds);
      map.panTo(new google.maps.LatLng(DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng));
    }
  };
  const getIconUrl = active =>
    (active ? '/statics/images/spotlight-poi-A-E.png' : '/statics/images/spotlight-poi-A-E-gray.png');
  const googleMapProps = {
    ref: setZoom,
    defaultZoom: 15,
    defaultCenter: {
      lat: state.parentprops.center ? Number(state.parentprops.center.lat) : DEFAULT_MAP_CENTER.lat,
      lng: state.parentprops.center ? Number(state.parentprops.center.lng) : DEFAULT_MAP_CENTER.lng,
    },
    options: {
      mapTypeControl: true,
    },
  };

  return (
    <GoogleMap {...googleMapProps}>
      {state.parentprops.isShowFreeDragPoint && (
        <Marker
          onDragStart={state.markDragStart.bind(this)}
          onDragEnd={state.markDragEnd.bind(this, state)}
          onClick={state.markDragClick.bind(this)}
          draggable={state.parentprops.isShowFreeDragPoint}
          position={{
            lat: Number(state.dragPointPosition.lat),
            lng: Number(state.dragPointPosition.lng),
          }}
        >
          {state.isShowDragInfo && (
            <InfoWindow onCloseClick={state.markDragClick}>
              <WindowInfo
                entered={state.parentprops.formAddress}
                coordinate={state.dragPointPosition}
                draggable
              />
            </InfoWindow>
          )}
        </Marker>
      )}
      {state.parentprops.markers.map((marker, index) => {
        const iconValue =
          state.parentprops.comeFrom === 'viewOnMap'
            ? null
            : {
              url: getIconUrl(marker.active),
              origin: new google.maps.Point(0, index * 40),
              size: new google.maps.Size(22, 40),
            };
        return (
          <Marker
            key={`google-map-marker-${String(index)}`}
            icon={iconValue}
            onClick={() => {
              if (state.parentprops.comeFrom === 'viewOnMap') {
                return false;
              }
              return state.onToggleOpen.bind(marker, index, this)();
            }}
            zIndex={marker.active ? state.markersLength : index}
            position={{
              lat: Number(marker.position.lat),
              lng: Number(marker.position.lng),
            }}
          >
            {state.isOpen && state.whichMarker === index && (
              <InfoWindow onCloseClick={state.onToggleOpen}>
                <WindowInfo
                  entered={state.parentprops.formAddress}
                  coordinate={{
                    lat: Number(marker.position.lat),
                    lng: Number(marker.position.lng),
                  }}
                  draggable={false}
                />
              </InfoWindow>
            )}
          </Marker>
        );
      })}
    </GoogleMap>
  );
});

const ReactGoogleMaps = props => [<MyMapComponent parentprops={props} key="map" />];

ReactGoogleMaps.propTypes = {
  googleMapParam: PropTypes.shape({
    googleMapAPIKey: PropTypes.string.isRequired,
    googleMapClientID: PropTypes.string.isRequired,
  }).isRequired,
  lang: PropTypes.string,
  stroke: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  paths: PropTypes.arrayOf(PropTypes.array),
  markers: PropTypes.arrayOf(PropTypes.shape()),
  center: PropTypes.shape(),
  comeFrom: PropTypes.string.isRequired,
  formAddress: PropTypes.shape(),
};

ReactGoogleMaps.defaultProps = {
  lang: 'en_US',
  stroke: 0,
  paths: [],
  markers: null,
  center: null,
  formAddress: null
};
export default ReactGoogleMaps;

const WindowInfo = ({ entered: { street, suite, city, state, postalCode, countryName }, coordinate: { lat, lng }, draggable }) => (
  <div className="win">
    <h6><I18N>gmap.lable.currentLocation</I18N></h6>
    <p className="has-border">
      <span><I18N>ssp.location.enter</I18N>: </span>
      {Object.values({
        street,
        suite,
        city,
        state,
        postalCode,
        countryName,
      })
        .filter(str => !!str)
        .join(', ')}
    </p>
    <p>
      <span><I18N>ssp.address.latitude</I18N>: </span> {lat}
    </p>
    <p>
      <span><I18N>ssp.address.longitude</I18N>: </span> {lng}
    </p>
    {
      draggable &&
      <p className="text-center text-info"><I18N>gmap.info.hints</I18N></p>
    }
  </div>
);
WindowInfo.propTypes = {
  entered: PropTypes.shape().isRequired,
  coordinate: PropTypes.shape().isRequired,
  draggable: PropTypes.bool.isRequired,
};
